<template>
  <section class="space-y-4">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 class="text-lg font-semibold text-zinc-900">Quiz Analytics</h3>
        <p class="mt-0.5 text-sm text-zinc-500">Track Quiz starts, exits, completions, scores, and member outcomes.</p>
      </div>
      <button
        type="button"
        class="h-10 rounded-lg bg-zinc-100 px-4 text-sm font-semibold text-zinc-800 hover:bg-zinc-200 disabled:opacity-50"
        :disabled="loading"
        @click="loadAnalytics"
      >
        {{ loading ? 'Refreshing...' : 'Refresh' }}
      </button>
    </div>

    <div class="flex flex-wrap items-end gap-3 rounded-xl border border-zinc-200 bg-white p-4">
      <div class="min-w-[260px]">
        <label class="mb-1 block text-xs font-semibold text-zinc-600">Quiz</label>
        <PlatformaSelect v-model="selectedInstanceId" :options="instanceOptions" />
      </div>
      <div class="min-w-[190px]">
        <label class="mb-1 block text-xs font-semibold text-zinc-600">Date range</label>
        <PlatformaSelect v-model="rangeKey" :options="rangeOptions" />
      </div>
      <template v-if="rangeKey === 'custom'">
        <div>
          <label class="mb-1 block text-xs font-semibold text-zinc-600">From</label>
          <input v-model="customFrom" type="date" class="h-10 rounded-lg border border-zinc-300 bg-white px-3 text-sm text-zinc-800">
        </div>
        <div>
          <label class="mb-1 block text-xs font-semibold text-zinc-600">To</label>
          <input v-model="customTo" type="date" class="h-10 rounded-lg border border-zinc-300 bg-white px-3 text-sm text-zinc-800">
        </div>
        <button
          type="button"
          class="h-10 rounded-lg bg-[#2D8CFF] px-4 text-sm font-semibold text-white disabled:opacity-50"
          :disabled="!customFrom || !customTo || loading"
          @click="loadAnalytics"
        >
          Apply
        </button>
      </template>
    </div>

    <div v-if="error" class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{{ error }}</div>

    <div class="grid grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-6">
      <article v-for="card in summaryCards" :key="card.label" class="rounded-xl border border-zinc-200 bg-white p-4">
        <p class="text-xs font-semibold uppercase tracking-wide text-zinc-500">{{ card.label }}</p>
        <p class="mt-1 text-2xl font-semibold text-zinc-900">{{ card.value }}</p>
        <p v-if="card.detail" class="mt-1 text-xs text-zinc-500">{{ card.detail }}</p>
      </article>
    </div>

    <div class="grid gap-4 xl:grid-cols-2">
      <section class="overflow-hidden rounded-xl border border-zinc-200 bg-white">
        <div class="border-b border-zinc-200 px-4 py-3">
          <h4 class="text-sm font-semibold text-zinc-800">Result Distribution</h4>
          <p class="mt-0.5 text-xs text-zinc-500">How often each Quiz result was reached.</p>
        </div>
        <div v-if="loading && !resultDistribution.length" class="px-4 py-8 text-sm text-zinc-500">Loading result data...</div>
        <div v-else-if="!resultDistribution.length" class="px-4 py-8 text-sm text-zinc-500">No Quiz results in this range.</div>
        <div v-else class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-zinc-50 text-zinc-600">
              <tr><th class="px-4 py-2 text-left font-medium">Result</th><th class="px-4 py-2 text-right font-medium">Times</th><th class="px-4 py-2 text-right font-medium">Members</th><th class="px-4 py-2 text-right font-medium">Share</th></tr>
            </thead>
            <tbody>
              <tr v-for="row in resultDistribution" :key="row.resultId || row.resultTitle" class="border-t border-zinc-100">
                <td class="px-4 py-2 font-medium text-zinc-900">{{ row.resultTitle }}</td>
                <td class="px-4 py-2 text-right text-zinc-700">{{ row.resultCount }}</td>
                <td class="px-4 py-2 text-right text-zinc-700">{{ row.uniqueMembers }}</td>
                <td class="px-4 py-2 text-right text-zinc-700">{{ formatPercent(row.percentOfFinished) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="overflow-hidden rounded-xl border border-zinc-200 bg-white">
        <div class="border-b border-zinc-200 px-4 py-3">
          <h4 class="text-sm font-semibold text-zinc-800">Performance by Quiz</h4>
          <p class="mt-0.5 text-xs text-zinc-500">Compare engagement across Quiz products.</p>
        </div>
        <div v-if="loading && !byQuiz.length" class="px-4 py-8 text-sm text-zinc-500">Loading Quiz performance...</div>
        <div v-else-if="!byQuiz.length" class="px-4 py-8 text-sm text-zinc-500">No Quiz products found.</div>
        <div v-else class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-zinc-50 text-zinc-600">
              <tr><th class="px-4 py-2 text-left font-medium">Quiz</th><th class="px-4 py-2 text-right font-medium">Started</th><th class="px-4 py-2 text-right font-medium">Finished</th><th class="px-4 py-2 text-right font-medium">Left</th><th class="px-4 py-2 text-right font-medium">Completion</th></tr>
            </thead>
            <tbody>
              <tr v-for="row in byQuiz" :key="row.featureInstanceId" class="border-t border-zinc-100">
                <td class="px-4 py-2 font-medium text-zinc-900">{{ row.quizTitle }}</td>
                <td class="px-4 py-2 text-right text-zinc-700">{{ row.startedCount }}</td>
                <td class="px-4 py-2 text-right text-zinc-700">{{ row.finishedCount }}</td>
                <td class="px-4 py-2 text-right text-zinc-700">{{ row.leftCount }}</td>
                <td class="px-4 py-2 text-right text-zinc-700">{{ formatPercent(row.completionRatePercent) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <section class="overflow-hidden rounded-xl border border-zinc-200 bg-white">
      <div class="border-b border-zinc-200 px-4 py-3">
        <h4 class="text-sm font-semibold text-zinc-800">Recent Member Results</h4>
        <p class="mt-0.5 text-xs text-zinc-500">The latest completed Quiz attempts in the selected range.</p>
      </div>
      <div v-if="loading && !recentAttempts.length" class="px-4 py-8 text-sm text-zinc-500">Loading member results...</div>
      <div v-else-if="!recentAttempts.length" class="px-4 py-8 text-sm text-zinc-500">No completed Quiz attempts yet.</div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="bg-zinc-50 text-zinc-600">
            <tr><th class="px-4 py-2 text-left font-medium">Member</th><th class="px-4 py-2 text-left font-medium">Quiz</th><th class="px-4 py-2 text-left font-medium">Result</th><th class="px-4 py-2 text-right font-medium">Score</th><th class="px-4 py-2 text-left font-medium">Completed</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in recentAttempts" :key="row.attemptId" class="border-t border-zinc-100 hover:bg-zinc-50">
              <td class="px-4 py-2 text-zinc-900"><p class="font-medium">{{ row.memberName }}</p><p v-if="row.memberEmail && row.memberEmail !== row.memberName" class="text-xs text-zinc-500">{{ row.memberEmail }}</p></td>
              <td class="px-4 py-2 text-zinc-700">{{ row.quizTitle }}</td>
              <td class="px-4 py-2 text-zinc-700">{{ row.resultTitle }}</td>
              <td class="px-4 py-2 text-right text-zinc-700">{{ row.quizMode === 'outcome' ? 'Outcome' : formatPercent(row.scorePercent) }}</td>
              <td class="px-4 py-2 text-zinc-600">{{ formatDateTime(row.completedAt) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import PlatformaSelect from '@/Components/PlatformaSelect.vue';
import { useQuizAnalyticsStore } from '@/stores/quizAnalyticsStore';

const props = defineProps({ active: { type: Boolean, default: false } });
const store = useQuizAnalyticsStore();
const { analytics, loading, error } = storeToRefs(store);
const selectedInstanceId = ref('all');
const rangeKey = ref('30d');
const customFrom = ref('');
const customTo = ref('');
const rangeOptions = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '365d', label: 'Last 365 days' },
  { value: 'lifetime', label: 'Lifetime' },
  { value: 'custom', label: 'Custom' },
];

const instanceOptions = computed(() => [
  { value: 'all', label: 'All Quizzes' },
  ...(Array.isArray(analytics.value?.instances) ? analytics.value.instances : []).map((row) => ({
    value: String(row.featureInstanceId),
    label: row.quizTitle,
  })),
]);
const summary = computed(() => analytics.value?.summary || {});
const resultDistribution = computed(() => Array.isArray(analytics.value?.resultDistribution) ? analytics.value.resultDistribution : []);
const byQuiz = computed(() => Array.isArray(analytics.value?.byQuiz) ? analytics.value.byQuiz : []);
const recentAttempts = computed(() => Array.isArray(analytics.value?.recentAttempts) ? analytics.value.recentAttempts : []);
const formatPercent = (value) => value == null || value === '' ? '—' : `${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
const summaryCards = computed(() => [
  { label: 'Quiz Started', value: Number(summary.value.startedCount || 0).toLocaleString(), detail: `${Number(summary.value.uniqueStarters || 0).toLocaleString()} unique members` },
  { label: 'Left Quiz', value: Number(summary.value.leftCount || 0).toLocaleString(), detail: `${formatPercent(summary.value.leftRatePercent)} of starts` },
  { label: 'Finished Quiz', value: Number(summary.value.finishedCount || 0).toLocaleString(), detail: `${Number(summary.value.uniqueFinishers || 0).toLocaleString()} unique members` },
  { label: 'Got Result', value: Number(summary.value.resultsReceivedCount || 0).toLocaleString(), detail: 'Results shown to members' },
  { label: 'Completion Rate', value: formatPercent(summary.value.completionRatePercent), detail: 'Finishes compared with starts' },
  { label: 'Average Score', value: formatPercent(summary.value.averageScorePercent), detail: summary.value.passRatePercent == null ? 'Outcome Quizzes are not scored' : `${formatPercent(summary.value.passRatePercent)} pass rate` },
]);

const formatDateTime = (value) => {
  const date = value ? new Date(value) : null;
  return !date || Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

async function loadAnalytics() {
  if (!props.active) return;
  if (rangeKey.value === 'custom' && (!customFrom.value || !customTo.value)) return;
  const params = { rangeKey: rangeKey.value };
  if (selectedInstanceId.value !== 'all') params.featureInstanceId = selectedInstanceId.value;
  if (rangeKey.value === 'custom') {
    params.from = customFrom.value;
    params.to = customTo.value;
  }
  await store.load(params).catch(() => {});
}

watch(() => props.active, (active) => { if (active) loadAnalytics(); }, { immediate: true });
watch(selectedInstanceId, () => { if (props.active) loadAnalytics(); });
watch(rangeKey, (value) => { if (props.active && value !== 'custom') loadAnalytics(); });
</script>
